import { type CSSProperties, memo } from 'react';
import type {
  IconDefinition,
  SizeProp,
} from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, type ColProps, Flex, theme, Typography } from 'antd';
import { type TextProps } from 'antd/es/typography/Text';
import { copyIcons } from 'pos-client/utils/copyIcons.tsx';

export interface PosTextProps extends Omit<TextProps, 'color' | 'style'> {
  icon?: IconDefinition;
  iconColor?: string;
  iconPosition?: 'left' | 'right';
  iconSize?: SizeProp;
  iconStyle?: CSSProperties;
  color?: string;
  column?: boolean;
  colProps?: ColProps;
  styles?: {
    container?: CSSProperties;
    text?: CSSProperties;
  };
}

const PosTextContent = memo(
  ({
    color,
    icon,
    iconColor,
    iconPosition = 'left',
    iconSize = 'sm',
    iconStyle,
    children,
    styles,
    ...props
  }: PosTextProps) => {
    const { token } = theme.useToken();
    const { Text } = Typography;
    return (
      <Flex
        gap="small"
        align="center"
        wrap="nowrap"
        style={{ ...styles?.container }}
      >
        {icon && iconPosition === 'left' && (
          <FontAwesomeIcon
            icon={icon}
            size={iconSize}
            color={iconColor ?? color ?? token.Card?.extraColor}
            style={iconStyle}
          />
        )}
        <Text
          style={{ color: color, ...styles?.text }}
          {...props}
          copyable={
            props.copyable
              ? typeof props.copyable === 'boolean'
                ? {
                    icon: copyIcons,
                  }
                : {
                    ...props.copyable,
                    icon: copyIcons,
                  }
              : undefined
          }
        >
          {children}
        </Text>
        {icon && iconPosition === 'right' && (
          <FontAwesomeIcon
            icon={icon}
            size={iconSize}
            color={iconColor ?? color ?? token.Card?.extraColor}
            style={iconStyle}
          />
        )}
      </Flex>
    );
  }
);

export const PosText = memo(
  ({ column = false, colProps, ...rest }: PosTextProps) => {
    return column ? (
      <Col {...colProps}>
        <PosTextContent {...rest} />
      </Col>
    ) : (
      <PosTextContent {...rest} />
    );
  }
);

export default PosText;
